import GlobalService from 'services/GlobalService';
import countries from '../../../data/countries';
import { reportToSentry } from '../../../utils/reportToSentry';

class BookingService extends GlobalService {
  getBookingByAppoitmentId(id) {
    const path = this.getEndpointUrl({ path: 'appointment/v0' });

    return this.axios
      .post(path, {
        appointmentId: id,
      })
      .then(response => {
        return {
          data: response?.data,
          error: null,
        };
      })
      .catch(error => {
        reportToSentry({
          error,
          level: 'error',
          extra: { method: 'getBookingByAppoitmentId', appointmentId: id },
        });
        return {
          data: null,
          error: error?.response?.data,
        };
      });
  }

  getLocationTimeSlots(locationCode, serviceCode) {
    const path = this.getEndpointUrl({ path: 'slots/v2' });

    return this.axios
      .post(path, {
        location: locationCode,
        service: serviceCode,
      })
      .then(response => {
        const { data } = response;
        if (data?.statusCode !== '200' || data?.error?.length > 0)
          // the API doesn't throw an error status for the reques
          // but returns error message in data.error
          // and the request status is stored in the data.statusCode
          // that is why we have to handle this error request
          // by returning the error value instead of data value
          return {
            data: null,
            error: {
              message: data.error,
              status: data.statusCode,
            },
          };

        return {
          data,
          error: null,
        };
      })
      .catch(error => {
        reportToSentry({
          error,
          level: 'error',
          extra: { method: 'getLocationTimeSlots', locationCode, serviceCode },
        });
        return {
          data: null,
          error: error?.response?.data,
        };
      });
  }

  getLocationsAvailableSlotsInfo(serviceCode) {
    const path = this.getEndpointUrl({ path: 'slots/available' });

    return this.axios
      .get(path, {
        params: {
          service: serviceCode,
          country: countries[process.env.GATSBY_COUNTRY_CODE].shortName,
        },
      })
      .then(response => {
        const { data } = response;
        if (response?.status !== 200)
          return {
            data: null,
            error: {
              message: data.error,
              status: data.statusCode,
            },
          };

        return {
          data,
          error: null,
        };
      })
      .catch(error => {
        reportToSentry({
          error,
          extra: { method: 'getLocationsAvailableSlotsInfo', serviceCode },
        });
        return {
          data: null,
          error: error.code,
        };
      });
  }

  getTimePerLocationV3Slots(locationCode, serviceCode, desiredDays = -1, desiredSlots = -1) {
    const path = this.getEndpointUrl({ path: 'slots/v3' });
    const requestBody = {
      slotRequests: [
        {
          location: locationCode,
          service: serviceCode,
        },
      ],
      desiredDays: desiredDays,
      desiredSlots: desiredSlots,
    };
    return this.axios
      .post(path, requestBody)
      .then(response => {
        const { data } = response;
        if (data?.error?.length > 0)
          return {
            data: null,
            error: {
              message: data.error,
              status: data.statusCode,
            },
          };
        return {
          data,
          error: null,
        };
      })
      .catch(error => {
        reportToSentry({
          error,
          extra: { method: 'getTimePerLocationV3Slots', locationCode, serviceCode, desiredDays, desiredSlots },
        });
        return {
          data: null,
          error: error?.response?.data,
        };
      });
  }

  getLocationTimeV3SeveralSlots(locationServicePairs, desiredDays = -1, desiredSlots = -1) {
    const path = this.getEndpointUrl({ path: 'slots/v3' });
    // construct the requestBody using 'map' to transform locationServicePairs
    const requestBody = {
      slotRequests: locationServicePairs.map(pair => ({
        location: pair.locationCode,
        service: pair.serviceCode,
      })),
      desiredDays: desiredDays,
      desiredSlots: desiredSlots,
    };

    return this.axios
      .post(path, requestBody)
      .then(response => {
        const { data } = response;
        if (data?.error?.length > 0)
          return {
            data: null,
            error: {
              message: data.error,
              status: data.statusCode,
            },
          };

        return {
          data,
          error: null,
        };
      })
      .catch(error => {
        reportToSentry({
          error,
          extra: { method: 'getLocationTimeV3SeveralSlots', locationServicePairs, desiredDays, desiredSlots },
        });
        return {
          data: null,
          error: error?.response?.data,
        };
      });
  }

  getDatesLocationV3Slots(locationServicePairs, dates) {
    const path = this.getEndpointUrl({ path: 'slots/v3/for-dates/multi' });

    // construct the requestBody using 'map' to transform locationServicePairs
    const requestBody = locationServicePairs.map(pair => ({
      location: pair.locationCode,
      service: pair.serviceCode,
      dates: dates,
    }));

    return this.axios
      .post(path, requestBody)
      .then(response => {
        const { data } = response;
        if (response?.status !== 200)
          return {
            data: null,
            error: {
              message: data.error,
              status: data.statusCode,
            },
          };

        return {
          data,
          error: null,
        };
      })
      .catch(error => {
        reportToSentry({
          error,
          extra: { method: 'getDatesLocationV3Slots', locationServicePairs, dates },
        });
        return {
          data: null,
          error: error.code,
        };
      });
  }

  checkDuplicateBooking(body, params) {
    const path = 'appointments/nearest' + (params.serviceCode && `?service=${params.serviceCode}`);
    const endpointUrl = this.getEndpointUrl({ path });

    return this.axios
      .post(endpointUrl, { ...body })
      .then(response => {
        const { data } = response;

        if (!data || data?.error)
          return {
            data: null,
            error: {
              status: 404,
            },
          };

        return {
          data,
          error: null,
        };
      })
      .catch(error => {
        reportToSentry({
          error,
          extra: { method: 'checkDuplicateBooking', body, params },
        });
        return {
          data: null,
          error: error?.response?.data,
        };
      });
  }

  saveBooking(body, token) {
    const path = this.getEndpointUrl({ path: 'book/v2' });
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    return this.axios
      .post(path, { ...body }, { headers })
      .then(response => {
        const { data } = response;

        if (data?.statusCode !== '200' || data?.error?.length > 0)
          return {
            data: null,
            error: {
              message: data.error,
              status: data.statusCode,
            },
          };

        return {
          data,
          error: null,
        };
      })
      .catch(error => {
        reportToSentry({
          error,
          extra: { method: 'saveBooking', body, token },
        });
        return {
          data: null,
          error: error?.response?.data,
        };
      });
  }

  getUserProfile(refreshToken) {
    const path = this.getEndpointUrl({ path: 'profile' }).replace('v1', 'v2');

    return this.axios
      .get(path, {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      })
      .then(response => {
        const { data } = response;
        if (response?.status !== 200)
          return {
            data: null,
            error: {
              status: response.status,
              message: response.data.error,
            },
          };
        return {
          data,
          error: null,
        };
      })
      .catch(error => {
        reportToSentry({
          error,
          extra: { method: 'getUserProfile', refreshToken },
        });
        return {
          data: null,
          error: error.code,
        };
      });
  }

  getAllowedServices() {
    const path = this.getEndpointUrl({ path: 'book/v3/allowed' });

    return this.axios
      .get(path)
      .then(response => {
        const { data } = response;

        if (response?.status !== 200)
          return {
            data: null,
            error: {
              status: response.status,
              message: response.data.error,
            },
          };
        return {
          data,
          error: null,
        };
      })
      .catch(error => {
        reportToSentry({
          error,
          extra: { method: 'getAllowedServices' },
        });
        return {
          data: null,
          error: error.code,
        };
      });
  }

  saveHDYHAU(userId, body) {
    const endpointUrl = this.getEndpointUrl({
      path: `leads/questionnaire/${userId}`,
    });

    return this.axios
      .put(endpointUrl, { ...body })
      .then(response => {
        if (response?.status !== '200' || response?.code)
          /*
						API error response:
						{
							code: "BOOKING_404"
							message: "Lead was not found by given externalId: 0031w00000gQ89rAAC"
						}
					*/
          return {
            data: null,
            error: {
              message: response?.message,
              status: 404,
            },
          };

        /*
					API success response:
					{
						data: "",
						status: 200,
						statusText: "",
						headers: {
							cache-control: "no-cache, no-store, max-age=0, must-revalidate",
							expires: "0",
							pragma: "no-cache"
						},
						config: {
							url: "leads/questionnaire/00Q1w000005GspQEAS?",
							method: "put",
							data: "{\"How did you hear about us?\":\"on social media\"}",
							headers: {
								Accept: "application/json, text/plain",
								"Content-Type": "application/json"
							},
							baseURL: "https://api.sqa1.drsmile.co/booking/api/v1/",
							transformRequest: [
								null
							],
							transformResponse: [
								null
							],
							timeout: 0,
							xsrfCookieName: "XSRF-TOKEN",
							xsrfHeaderName: "X-XSRF-TOKEN",
							maxContentLength: -1,
							maxBodyLength: -1
						},
						request: {}
					}
				*/
        return {
          data: {
            status: 200,
          },
          error: null,
        };
      })
      .catch(error => {
        reportToSentry({
          error,
          extra: { method: 'saveHDYHAU', userId, body },
        });
        return {
          data: null,
          error: error?.response?.data,
        };
      });
  }
}

const bookingService = new BookingService();

export default bookingService;
